import api_base from './base';
import type {
  CreditConfiguration,
  CreditConfigurationCreate,
  CabinetCreditQuota,
  CabinetCreditQuotaUpdate,
  CreditUsageStats,
  CabinetCreditSummary,
  CreditListParams,
  CreditStatsParams
} from '../types/credit';

// Base URL for credit endpoints
const CREDIT_BASE_URL = '/api/v1/super-admin/credits';

/**
 * Credit Configuration Management
 */

/**
 * GET /api/super-admin/credits/config/ - Get active credit configuration
 */
export const getCreditConfiguration = async (): Promise<CreditConfiguration> => {
  const response = await api_base.get(`${CREDIT_BASE_URL}/config/`);
  return response.data;
};

/**
 * POST /api/super-admin/credits/config/ - Create new credit configuration
 */
export const createCreditConfiguration = async (
  data: CreditConfigurationCreate
): Promise<CreditConfiguration> => {
  const response = await api_base.post(`${CREDIT_BASE_URL}/config/`, data);
  return response.data;
};

/**
 * Cabinet Credit Quota Management
 */

/**
 * GET /api/super-admin/credits/cabinets/ - List all cabinet credit quotas
 */
export const getCabinetCreditQuotas = async (
  params?: CreditListParams
): Promise<{
  count: number;
  next: string | null;
  previous: string | null;
  results: CabinetCreditQuota[];
}> => {
  const response = await api_base.get(`${CREDIT_BASE_URL}/cabinets/`, { params });
  return response.data;
};

/**
 * GET /api/super-admin/credits/cabinets/{cabinet_id}/ - Get detailed credit quota for specific cabinet
 */
export const getCabinetCreditQuota = async (cabinetId: string): Promise<CabinetCreditQuota> => {
  const response = await api_base.get(`${CREDIT_BASE_URL}/cabinets/${cabinetId}/`);
  return response.data;
};

/**
 * PATCH /api/super-admin/credits/cabinets/{cabinet_id}/update/ - Update cabinet's monthly credit limit
 */
export const updateCabinetCreditQuota = async (
  cabinetId: string,
  data: CabinetCreditQuotaUpdate
): Promise<CabinetCreditQuota> => {
  const response = await api_base.patch(`${CREDIT_BASE_URL}/cabinets/${cabinetId}/update/`, data);
  return response.data;
};

/**
 * Credit Usage Statistics
 */

/**
 * GET /api/super-admin/credits/cabinets/{cabinet_id}/stats/ - Get detailed usage statistics
 */
export const getCabinetCreditStats = async (
  cabinetId: string,
  params?: CreditStatsParams
): Promise<CreditUsageStats> => {
  const response = await api_base.get(`${CREDIT_BASE_URL}/cabinets/${cabinetId}/stats/`, { params });
  return response.data;
};

/**
 * GET /api/super-admin/credits/summary/ - Get credit usage summary for all cabinets
 */
export const getAllCabinetsCreditSummary = async (
  params?: CreditStatsParams
): Promise<CabinetCreditSummary[]> => {
  const response = await api_base.get(`${CREDIT_BASE_URL}/summary/`, { params });
  return response.data;
};

/**
 * Helper function to format credit values for display
 */
export const formatCredits = (credits: string | number): string => {
  const value = typeof credits === 'string' ? parseFloat(credits) : credits;
  return value.toFixed(2);
};

/**
 * Helper function to format percentage
 */
export const formatPercentage = (percentage: number): string => {
  return `${percentage.toFixed(1)}%`;
};

/**
 * Helper function to get status color based on usage percentage
 */
export const getCreditStatusColor = (percentage: number): 'success' | 'warning' | 'danger' => {
  if (percentage < 70) return 'success';
  if (percentage < 90) return 'warning';
  return 'danger';
};

/**
 * Helper function to parse reset time
 */
export const parseResetTime = (resetTime: string): Date => {
  return new Date(resetTime);
};
